import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Label } from '@/shared/components';
import { toApiError, useApi, type InvoiceTemplateResponse } from '@/shared/api';
import { invoiceTemplatesKey } from './useInvoiceTemplates';

/**
 * Logo-Upload je Vorlage. Vorschau über GET; nach einem erneuten Upload hängt ein Zeitstempel an
 * der URL, sonst zeigt der Browser-Cache das alte Bild.
 */
export function InvoiceTemplateLogoSection({ template }: { template: InvoiceTemplateResponse }) {
  const api = useApi();
  const qc = useQueryClient();
  const [cacheBust, setCacheBust] = useState(0);

  const showError = async (err: unknown) => {
    const e = await toApiError(err);
    toast.error(e.detail ?? e.title);
  };

  const upload = useMutation({
    mutationFn: (file: File) => api.invoiceTemplates.uploadLogo(template.id, file),
    onSuccess: () => {
      toast.success('Logo hochgeladen');
      setCacheBust(Date.now());
      void qc.invalidateQueries({ queryKey: invoiceTemplatesKey });
    },
    onError: showError,
  });

  const remove = useMutation({
    mutationFn: () => api.invoiceTemplates.removeLogo(template.id),
    onSuccess: () => {
      toast.success('Logo entfernt');
      void qc.invalidateQueries({ queryKey: invoiceTemplatesKey });
    },
    onError: showError,
  });

  const logoUrl = template.hasLogo
    ? `${api.invoiceTemplates.logoUrl(template.id)}${cacheBust ? `?v=${cacheBust}` : ''}`
    : null;

  return (
    <div className="border-line mt-6 space-y-3 border-t pt-4">
      <Label htmlFor="invoice-template-logo">Logo</Label>
      {logoUrl && (
        <img
          src={logoUrl}
          alt={`Logo der Vorlage ${template.name}`}
          className="border-line max-h-24 max-w-full rounded border bg-white object-contain p-1"
        />
      )}
      <input
        id="invoice-template-logo"
        type="file"
        accept="image/png,image/jpeg"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
        }}
        disabled={upload.isPending}
        className="text-ink-soft file:border-line file:bg-card-alt file:text-ink block w-full text-xs file:mr-3 file:min-h-11 file:rounded-md file:border file:px-3 file:text-xs"
      />
      <p className="text-ink-faint text-xs">PNG oder JPEG, höchstens 5 MB.</p>
      {template.hasLogo && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="hover:text-sealwax"
          onClick={() => remove.mutate()}
          loading={remove.isPending}
        >
          Logo entfernen
        </Button>
      )}
    </div>
  );
}
